import { AmountInputField, Partnership } from '@hex/calculators-shared/components';
import { LTV_THRESHOLD } from '@hex/calculators-shared/constants';
import { useSapphireMedia } from '@hex/calculators-shared/hooks';
import { InterestRatesRequest, InterestRatesResponse } from '@hex/calculators-shared/types';
import { formatMoney } from '@hex/calculators-shared/utils';
import { Button, Flex, Icon, tokens, HelpButton, Typography } from '@sapphire-react/core';
import { Home, Sack } from '@sapphire/icons/react';
import { RefObject, useEffect } from 'react';
import type { UseFormReset } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useInterestRateCalculatorForm } from './useInterestRateCalculatorForm.hook';

type Props = {
  buttonRef: RefObject<any>;
  responseData: InterestRatesResponse | null;
  loading?: boolean;
  onFormChange: () => void;
  onSubmit: (data: InterestRatesRequest, isDirty: boolean, reset: UseFormReset<InterestRatesRequest>) => void;
};

export function Form({ onSubmit, responseData, onFormChange, buttonRef, loading }: Props) {
  const { t } = useTranslation();
  const { isExtraSmall, isSmall } = useSapphireMedia();

  const {
    control,
    handleSubmit,
    trackCalculatorStarted,
    reset,
    ltv,
    isDirty,
    propertyValueFieldRules,
    loanAmountFieldRules,
  } = useInterestRateCalculatorForm();

  // Resets results
  useEffect(() => {
    if (isDirty && responseData) {
      onFormChange();
    }
  }, [isDirty, onFormChange, responseData]);

  return (
    <S.Form
      onClick={trackCalculatorStarted}
      onSubmit={handleSubmit((data) => onSubmit(data, isDirty, reset))}
      autoComplete={'off'}
    >
      <Flex flexDirection={'column'} gap={tokens.size.spacingMd}>
        <AmountInputField
          control={control}
          name={'propertyValue'}
          label={t('personalInformation.inputs.propertyValue.label')}
          width={'100%'}
          rules={propertyValueFieldRules}
          postfix={'kr'}
          max={9_999_999_999}
          prefix={
            <Icon size={isExtraSmall || isSmall ? 'md' : 'lg'}>
              <Home />
            </Icon>
          }
        />
        <AmountInputField
          control={control}
          name={'loanAmount'}
          label={t('personalInformation.inputs.loanAmount.label')}
          rules={loanAmountFieldRules}
          width={'100%'}
          postfix={'kr'}
          max={9_999_999_999}
          contextualHelp={
            <HelpButton
              variant={'help'}
              aria-label={'Hjälp!'}
              contentBody={
                <>
                  <Typography.Body size={'sm'}>
                    {t('personalInformation.inputs.loanAmount.tooltip._1', { percentage: LTV_THRESHOLD })}
                  </Typography.Body>
                  <Typography.Body marginTop={tokens.size.spacing2xs} size={'sm'}>
                    {t('personalInformation.inputs.loanAmount.tooltip._2', {
                      amount1: formatMoney(1000000),
                      amount2: formatMoney(1000000 * (LTV_THRESHOLD / 100)),
                    })}
                  </Typography.Body>
                </>
              }
            />
          }
          prefix={
            <Icon size={isExtraSmall || isSmall ? 'md' : 'lg'}>
              <Sack />
            </Icon>
          }
          note={
            <>
              {t('personalInformation.inputs.loanAmount.note')} {ltv !== undefined && `${ltv} %`}
            </>
          }
        />

        <Partnership control={control} withSecondaryApplicant={false} />
      </Flex>
      <Flex justifyContent={'center'} marginTop={tokens.size.spacing2xl}>
        <Button ref={buttonRef} type={'submit'} isDisabled={loading} name={'Beräkna'} data-test-id={'form-submit'}>
          {loading ? t('loading') : t('calculate')}
        </Button>
      </Flex>
    </S.Form>
  );
}

const S = {
  Form: styled.form`
    display: flex;
    flex-direction: column;
  `,
};
